import { viewTransition } from '@theme/utilities';

const IMAGE = '.product-media-container--zoomable.product-media-container--image';
const ZOOM_BUTTON = '.product-media-container__zoom-button';
const FINE_POINTER = '(hover: hover) and (pointer: fine)';

/** Decorative follower only. Horizon and the native cursor retain all input handling. */
class LazyOafGalleryCursor extends HTMLElement {
  #gallery;
  #controller;
  #observer;
  #fine = matchMedia(FINE_POINTER);
  #reduced = matchMedia('(prefers-reduced-motion: reduce)');
  #layer;
  #follow;
  #target;
  #point;
  #position;
  #frame = 0;
  #animation;
  #transition;
  #ready = false;
  #duration = 125;
  #easing = 'cubic-bezier(0.33, 1, 0.68, 1)';

  connectedCallback() {
    this.#gallery = this.closest('media-gallery');
    if (!this.#gallery) return;
    // Theme-editor/quick-view clones can contain derived UI from the previous host.
    this.#gallery.querySelectorAll('.lazy-oaf-gallery-cursor').forEach((node) => node.remove());
    this.#gallery.querySelectorAll('[data-oaf-cursor]').forEach((node) => node.removeAttribute('data-oaf-cursor'));
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#layer = document.createElement('div');
    this.#layer.className = 'lazy-oaf-gallery-cursor';
    this.#layer.hidden = true;
    this.#layer.setAttribute('aria-hidden', 'true');
    this.#follow = document.createElement('div');
    this.#follow.className = 'lazy-oaf-gallery-cursor__follow';
    this.#follow.append(document.createElement('span'));
    this.#layer.append(this.#follow);

    const style = getComputedStyle(this);
    const speed = style.getPropertyValue('--animation-speed').trim();
    if (speed) this.#duration = parseFloat(speed) * (speed.endsWith('ms') ? 1 : 1000);
    this.#easing = style.getPropertyValue('--ease-out-cubic').trim() || this.#easing;

    document.addEventListener('pointermove', this.#move, { passive: true, signal });
    document.addEventListener('pointerdown', this.#press, { passive: true, capture: true, signal });
    document.addEventListener('pointerup', this.#release, { passive: true, signal });
    document.addEventListener('pointercancel', this.#reset, { signal });
    document.addEventListener('pointerout', this.#leave, { signal });
    document.addEventListener('scroll', this.#schedule, { capture: true, passive: true, signal });
    document.addEventListener('click', this.#schedule, { signal });
    document.addEventListener('keydown', this.#reset, { signal });
    document.addEventListener('visibilitychange', this.#reset, { signal });
    window.addEventListener('blur', this.#reset, { signal });
    window.addEventListener('resize', this.#schedule, { signal });
    this.#fine.addEventListener('change', this.#reset, { signal });
    this.#reduced.addEventListener('change', this.#reset, { signal });
    this.#observer = new MutationObserver(this.#schedule);
    this.#observer.observe(this.#gallery, { subtree: true, attributes: true, attributeFilter: ['open'] });

    // Keep the complete native artwork if either enhancement asset fails to load.
    Promise.all([this.dataset.zoomIn, this.dataset.zoomOut].map((src) => new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = resolve;
      image.onerror = reject;
      image.src = src;
    }))).then(() => {
      if (signal.aborted) return;
      this.#ready = true;
      this.#schedule();
    }).catch(() => {});
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#reset();
    this.#layer?.remove();
    this.#ready = false;
  }

  #move = (event) => {
    if (event.pointerType !== 'mouse' || !this.#fine.matches) return this.#reset();
    this.#point = { x: event.clientX, y: event.clientY };
    this.#schedule();
  };

  #press = (event) => {
    if (event.pointerType !== 'mouse' || !this.#fine.matches) return this.#reset();
    if (event.button !== 0) return;
    this.#point = { x: event.clientX, y: event.clientY };
    this.#sync();
    if (this.#target) this.#layer.toggleAttribute('data-pressed', true);
  };

  #release = () => this.#layer?.removeAttribute('data-pressed');

  #leave = (event) => {
    if (!event.relatedTarget) this.#reset();
  };

  #schedule = () => {
    if (!this.#point || this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#sync();
    });
  };

  #reset = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#point = undefined;
    this.#hide();
  };

  #hide() {
    this.#target?.removeAttribute('data-oaf-cursor');
    this.#target = undefined;
    this.#position = undefined;
    this.#animation?.cancel();
    this.#animation = undefined;
    if (this.#layer) this.#layer.hidden = true;
    this.#release();
  }

  #sync() {
    if (!this.#ready || !this.#fine.matches || !this.#point || !this.isConnected) return this.#hide();
    // Use the complete native cursor during Horizon's image transition. Never capture
    // a stationary disc in the image snapshot or postpone the zoom action.
    if (viewTransition.current) {
      this.#hide();
      if (this.#transition !== viewTransition.current) {
        this.#transition = viewTransition.current;
        this.#transition.finally(this.#schedule).catch(() => {});
      }
      return;
    }

    const { x, y } = this.#point;
    const hit = document.elementFromPoint(x, y);
    const target = hit?.closest(`${ZOOM_BUTTON}, ${IMAGE}`);
    const control = hit?.closest('button, a, input, video, model-viewer, [role="button"]');
    if (!target || target.closest('media-gallery') !== this.#gallery ||
        (control && !control.matches(ZOOM_BUTTON))) return this.#hide();

    const dialog = target.closest('dialog');
    if (dialog && !dialog.open) return this.#hide();
    if (this.#target !== target) {
      this.#target?.removeAttribute('data-oaf-cursor');
      this.#target = target;
      target.setAttribute('data-oaf-cursor', target.closest('zoom-dialog') ? 'out' : 'in');
    }
    // A body overlay cannot paint above a modal dialog's top layer.
    const parent = dialog || document.body;
    if (this.#layer.parentElement !== parent) parent.append(this.#layer);

    if (this.#position && (x !== this.#position.x || y !== this.#position.y) && !this.#reduced.matches) {
      const current = new DOMMatrixReadOnly(getComputedStyle(this.#follow).transform);
      let dx = this.#position.x + current.m41 - x;
      let dy = this.#position.y + current.m42 - y;
      const distance = Math.hypot(dx, dy);
      const limit = Math.min(1, 4 / (distance || 1));
      dx *= limit;
      dy *= limit;
      this.#animation?.cancel();
      this.#animation = this.#follow.animate([
        { transform: `translate(${dx}px, ${dy}px)` },
        { transform: 'translate(0, 0)' },
      ], { duration: this.#duration, easing: this.#easing });
    }
    this.#layer.style.transform = `translate3d(${x - 40}px, ${y - 40}px, 0)`;
    this.#layer.hidden = false;
    this.#position = { x, y };
  }
}

if (!customElements.get('lazy-oaf-gallery-cursor')) {
  customElements.define('lazy-oaf-gallery-cursor', LazyOafGalleryCursor);
}
